import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

/**
 * MARCAR AVALIAÇÃO — Página standalone
 * Reutiliza a MESMA integração Formspree do AvaliacaoDialog
 * (endpoint, campos, validações e mensagens). Apenas adaptada
 * para funcionar como página em ecrã inteiro.
 */
const FORMSPREE_ENDPOINT = 'https://formspree.io/f/xwvypggq';

const objetivos = [
  { value: 'perda-peso', label: 'Perda de peso' },
  { value: 'massa-muscular', label: 'Aumento de massa muscular' },
  { value: 'reabilitacao', label: 'Reabilitação física' },
  { value: 'outro', label: 'Outro' }
];

const MarcarAvaliacao = () => {
  const [formData, setFormData] = useState({
    nome: '',
    telefone: '',
    email: '',
    objetivo: ''
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  const validateForm = () => {
    const newErrors = {};
    if (!formData.nome.trim()) newErrors.nome = 'Por favor preencha este campo.';
    if (!formData.telefone.trim()) newErrors.telefone = 'Por favor preencha este campo.';
    if (!formData.email.trim()) {
      newErrors.email = 'Por favor preencha este campo.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Por favor insira um email válido.';
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setSubmitting(true);
    setFeedback({ type: '', message: '' });
    const objetivoLabel = (objetivos.find(o => o.value === formData.objetivo) || {}).label || '';

    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nome: formData.nome,
          telemovel: formData.telefone,
          email: formData.email,
          objetivo: objetivoLabel || formData.objetivo,
          _subject: 'Nova marcação de avaliação física - Ultimate Fit',
          _source: 'marcar-avaliacao-page'
        })
      });
      if (res.ok) {
        setFeedback({ type: 'success', message: 'Pedido enviado com sucesso! Entraremos em contacto em breve.' });
        setFormData({ nome: '', telefone: '', email: '', objetivo: '' });
        setErrors({});
      } else {
        let detail = '';
        try {
          const data = await res.json();
          if (data && Array.isArray(data.errors)) detail = data.errors.map(err => err.message).join(' ');
        } catch (_) { /* ignore */ }
        setFeedback({ type: 'error', message: detail || 'Não foi possível enviar o pedido. Tente novamente.' });
      }
    } catch (err) {
      setFeedback({ type: 'error', message: 'Erro de ligação. Verifique a sua internet e tente novamente.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
    if (feedback.message) setFeedback({ type: '', message: '' });
  };

  return (
    <main className="min-h-screen bg-black flex flex-col items-center" data-testid="marcar-avaliacao-page">
      {/* Topo: logo discreto */}
      <header className="w-full px-5 sm:px-8 py-5">
        <Link to="/" className="inline-block" aria-label="Ultimate Fit - Página inicial">
          <img
            src="/assets/logo-uf-mobile.webp?v=2"
            alt="Ultimate Fit"
            width="84"
            height="86"
            className="block h-12 w-auto sm:h-14"
          />
        </Link>
      </header>

      {/* Container do formulário */}
      <section className="w-full flex-1 flex justify-center px-4 sm:px-6 pb-12">
        <div
          className="w-full bg-white animate-uf-fade-in"
          style={{ maxWidth: '900px', borderRadius: 0 }}
        >
          <div className="p-6 sm:p-10 md:p-14">
            <div className="mb-7">
              <h1
                className="text-3xl sm:text-4xl md:text-5xl text-black mb-3"
                style={{ fontFamily: "'BebasNeue-Book', sans-serif", fontWeight: "normal", letterSpacing: "0.02em", lineHeight: "1.05" }}
              >
                Marcar Avaliação Física Gratuita
              </h1>
              <p
                className="text-gray-600 text-base sm:text-lg"
                style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300, letterSpacing: "0.01em", lineHeight: "1.6" }}
              >
                Preenche o formulário e entraremos em contacto contigo para agendar a tua avaliação.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6" data-testid="marcar-avaliacao-form">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="ma-nome" className="text-sm font-semibold text-gray-700 mb-2 block">Nome *</Label>
                  <Input
                    id="ma-nome" name="nome" data-testid="ma-input-nome"
                    value={formData.nome}
                    onChange={(e) => handleChange('nome', e.target.value)}
                    className={`h-12 border-2 ${errors.nome ? 'border-red-500' : 'border-gray-300'} focus:border-black`}
                    placeholder="Insira o seu nome"
                    autoComplete="name"
                  />
                  {errors.nome && <p className="text-red-500 text-sm mt-1">{errors.nome}</p>}
                </div>
                <div>
                  <Label htmlFor="ma-tel" className="text-sm font-semibold text-gray-700 mb-2 block">Telemóvel *</Label>
                  <Input
                    id="ma-tel" name="telemovel" type="tel" inputMode="tel" data-testid="ma-input-telemovel"
                    value={formData.telefone}
                    onChange={(e) => handleChange('telefone', e.target.value)}
                    className={`h-12 border-2 ${errors.telefone ? 'border-red-500' : 'border-gray-300'} focus:border-black`}
                    placeholder="Insira o seu telemóvel"
                    autoComplete="tel"
                  />
                  {errors.telefone && <p className="text-red-500 text-sm mt-1">{errors.telefone}</p>}
                </div>
                <div>
                  <Label htmlFor="ma-email" className="text-sm font-semibold text-gray-700 mb-2 block">Email *</Label>
                  <Input
                    id="ma-email" name="email" type="email" inputMode="email" data-testid="ma-input-email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className={`h-12 border-2 ${errors.email ? 'border-red-500' : 'border-gray-300'} focus:border-black`}
                    placeholder="Insira o seu email"
                    autoComplete="email"
                  />
                  {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                </div>
              </div>

              <div>
                <Label className="text-sm font-semibold text-gray-700 mb-3 block">Objetivo a alcançar</Label>
                <div className="space-y-3">
                  {objetivos.map((objetivo) => (
                    <label key={objetivo.value} className="flex items-center space-x-3 cursor-pointer group">
                      <input
                        type="radio"
                        name="objetivo"
                        value={objetivo.value}
                        checked={formData.objetivo === objetivo.value}
                        onChange={(e) => handleChange('objetivo', e.target.value)}
                        data-testid={`ma-radio-${objetivo.value}`}
                        className="w-5 h-5 text-yellow-400 border-2 border-gray-300 focus:ring-2 focus:ring-yellow-400 cursor-pointer"
                      />
                      <span className="text-gray-700 text-base group-hover:text-black transition-colors">{objetivo.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {feedback.message && (
                <p
                  role={feedback.type === 'error' ? 'alert' : 'status'}
                  data-testid="ma-feedback"
                  className={`text-center text-sm mb-2 ${feedback.type === 'success' ? 'text-green-600' : 'text-red-600'}`}
                  style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}
                >
                  {feedback.message}
                </p>
              )}

              <div className="pt-2">
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="ma-submit-btn"
                  className="w-full bg-yellow-400 hover:bg-yellow-500 text-black text-[20px] h-14 border-2 border-yellow-400 hover:border-yellow-500 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ fontFamily: "'BebasNeue-Book', sans-serif", fontWeight: "normal", letterSpacing: "-0.02em" }}
                >
                  {submitting ? 'A ENVIAR...' : 'Enviar Pedido'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Botão Voltar ao site */}
      <div className="w-full flex justify-center pb-10 px-4">
        <Link
          to="/"
          data-testid="ma-back-link"
          className="inline-flex items-center gap-2 text-gray-300 hover:text-yellow-400 transition-colors uppercase"
          style={{
            fontFamily: "'BebasNeue-Book', sans-serif",
            fontSize: '16px',
            letterSpacing: '0.08em'
          }}
        >
          <ArrowLeft size={16} />
          Voltar ao site
        </Link>
      </div>
    </main>
  );
};

export default MarcarAvaliacao;
